import type { ProspectApplicationPerson } from '../services/tenantApi';

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

/** Profile fields autosave is allowed to send. Invalid emails and a half-entered birthday are omitted so the rest still persists. */
export interface SavableApplicantPatch {
  firstName: string;
  lastName: string;
  preferredFirstName: string;
  phone: string;
  email?: string;
  dateOfBirth?: string | null;
  address: string;
  city: string;
  unitNumber: string;
  province: string;
  postalCode: string;
  country: string;
  school: string;
  program: string;
  currentYearOfStudy: string;
  expectedGraduationTerm: string;
  expectedGraduationYear: string;
  emergencyContactName: string;
  emergencyContactRelationship: string;
  emergencyContactPhone: string;
  emergencyContactEmail?: string;
}

interface StoredDraft {
  patch: SavableApplicantPatch;
  ackedSnapshot: string | null;
}

function storageKey(applicantId: string): string {
  return `genie.prospectApplicantDraft.${applicantId}`;
}

function emailSavable(value: string): boolean {
  const email = value.trim();
  if (!email) return true;
  return email.length <= 254 && EMAIL_PATTERN.test(email);
}

export function savableApplicantPatch(person: ProspectApplicationPerson): SavableApplicantPatch {
  const patch: SavableApplicantPatch = {
    firstName: person.firstName,
    lastName: person.lastName,
    preferredFirstName: person.preferredFirstName,
    phone: person.phone,
    address: person.address,
    city: person.city,
    unitNumber: person.unitNumber,
    province: person.province,
    postalCode: person.postalCode,
    country: person.country,
    school: person.school,
    program: person.program,
    currentYearOfStudy: person.currentYearOfStudy,
    expectedGraduationTerm: person.expectedGraduationTerm,
    expectedGraduationYear: person.expectedGraduationYear,
    emergencyContactName: person.emergencyContactName,
    emergencyContactRelationship: person.emergencyContactRelationship,
    emergencyContactPhone: person.emergencyContactPhone,
  };
  if (emailSavable(person.email)) patch.email = person.email;
  if (person.dateOfBirth) patch.dateOfBirth = person.dateOfBirth;
  if (emailSavable(person.emergencyContactEmail)) patch.emergencyContactEmail = person.emergencyContactEmail;
  return patch;
}

export function rememberApplicantDraft(applicantId: string, patch: SavableApplicantPatch): void {
  const snapshot = JSON.stringify(patch);
  const existing = readStored(applicantId);
  if (existing?.ackedSnapshot === snapshot) return;
  if (existing && JSON.stringify(existing.patch) === snapshot) return;
  writeStored(applicantId, { patch, ackedSnapshot: existing?.ackedSnapshot ?? null });
}

export function acknowledgeApplicantDraft(applicantId: string, patch: SavableApplicantPatch): void {
  writeStored(applicantId, { patch, ackedSnapshot: JSON.stringify(patch) });
}

/** Local edits that have not been confirmed by the server. */
export function unackedApplicantPatch(applicantId: string): SavableApplicantPatch | null {
  const stored = readStored(applicantId);
  if (!stored) return null;
  if (stored.ackedSnapshot === JSON.stringify(stored.patch)) return null;
  return stored.patch;
}

function readStored(applicantId: string): StoredDraft | null {
  try {
    const raw = sessionStorage.getItem(storageKey(applicantId));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as StoredDraft;
    if (!parsed?.patch || typeof parsed.patch !== 'object') return null;
    return parsed;
  } catch {
    return null;
  }
}

function writeStored(applicantId: string, value: StoredDraft): void {
  try {
    sessionStorage.setItem(storageKey(applicantId), JSON.stringify(value));
  } catch {
    // Private mode or a full quota should not block typing.
  }
}

export function isTransientSaveError(err: unknown): boolean {
  if (!(err instanceof Error)) return false;
  const message = err.message.toLowerCase();
  return message === 'failed to fetch'
    || message.includes('network')
    || message.includes('load failed')
    || message.includes('networkerror');
}
